import { Box, Button, Card, CardBody, Flex, FormControl, FormErrorMessage, Heading, HStack, Icon, Text, Textarea } from "@chakra-ui/react";
import { useId, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import { MarketDialogueAnswerType, type MarketDialogueQuestionDto } from "../../../autogen/bff-api";
import { StatusAccentBar } from "../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../common/components/StatusDotLabel";
import { TextLengthHint } from "../../../common/input/TextLengthHint";
import { isTiptapContentEmpty, TipTap } from "../../../common/input/TipTap/TipTap";
import { tiptapVisibleLength } from "../../../common/input/TipTap/tiptapToPlainText";
import { answerTypeOf, takesDocuments, useCommentSubjectKindLabel } from "../marketDialogueLabels";
import { BusinessSecretMark } from "./BusinessSecretMark";

export const MAX_ANSWER_LENGTH = 10000;
const MAX_STORED_ANSWER_SIZE = 50000;

export interface QuestionAnswerState {
  text: string;
  choice: boolean | null;
  secretReason: string | null;
  secretDocuments: Record<string, string>;
}

const hasTooMuchFormatting = (answer: QuestionAnswerState) => answer.text.length > MAX_STORED_ANSWER_SIZE;

export const isTooLong = (answer: QuestionAnswerState) =>
  tiptapVisibleLength(answer.text) > MAX_ANSWER_LENGTH || hasTooMuchFormatting(answer);

export const lacksSecretReason = (answer: QuestionAnswerState) =>
  answer.secretReason?.trim() === "" || Object.values(answer.secretDocuments).some((reason) => reason.trim() === "");

interface Props {
  question: MarketDialogueQuestionDto;
  number: number;
  answer: QuestionAnswerState;
  documents: { id: string; name: string }[];
  canAnswer: boolean;
  onChange: (answer: QuestionAnswerState) => void;
  attachments: ReactNode;
}

const answerToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

export const needsChoice = (question: MarketDialogueQuestionDto, answer: QuestionAnswerState) =>
  answerTypeOf(question) === MarketDialogueAnswerType.YesNoComment &&
  answer.choice === null &&
  answer.text.trim().length > 0;

export const isAnswered = (question: MarketDialogueQuestionDto, answer: QuestionAnswerState, documentCount: number) => {
  if (documentCount > 0) return true;
  switch (answerTypeOf(question)) {
    case MarketDialogueAnswerType.YesNoComment:
      return answer.choice !== null;
    case MarketDialogueAnswerType.Document:
      return false;
    case MarketDialogueAnswerType.FreeText:
      return !isTiptapContentEmpty(answer.text);
  }
};

export const MarketDialogueQuestionCard = ({
  question,
  number,
  answer,
  documents,
  canAnswer,
  onChange,
  attachments,
}: Props) => {
  const { t } = useTranslation();
  const subjectKindLabel = useCommentSubjectKindLabel();
  const titleId = useId();
  const answerLabelId = useId();
  const documentCount = documents.length;
  const answerType = answerTypeOf(question);
  const answered = isAnswered(question, answer, documentCount);
  const tooLong = isTooLong(answer);
  const choiceMissing = needsChoice(question, answer);
  const reasonMissing = lacksSecretReason(answer);
  const hasIssue = tooLong || choiceMissing || reasonMissing;
  const statusLabel = tooLong
    ? t("Too long")
    : choiceMissing
      ? t("Choose Yes or No")
      : reasonMissing
        ? t("Reason missing")
        : answered
          ? t("Answered")
          : t("Not answered");

  return (
    <Card variant="outline" rounded="xl" position="relative" overflow="hidden">
      <StatusAccentBar variant={hasIssue ? "issue" : answered ? "ok" : "neutral"} />
      <CardBody>
        <Flex justify="space-between" align="flex-start" gap="3" mb="3">
          <Box>
            {question.subject && (
              <Text fontSize="xs" fontWeight="semibold" color="smMuted" textTransform="uppercase" mb="1">
                {subjectKindLabel(question.subject.kind)}
              </Text>
            )}
            <Heading size="sm" id={titleId} whiteSpace="pre-wrap">
              {number}. {question.text}
            </Heading>
          </Box>
          <StatusDotLabel variant={hasIssue ? "issue" : answered ? "ok" : "neutral"} label={statusLabel} />
        </Flex>
        {question.subject?.description && !isTiptapContentEmpty(question.subject.description) && (
          <Box mb="3" data-testid="comment-subject-description">
            <TipTap content={question.subject.description} editable={false} maxHeight="48" />
          </Box>
        )}
        <FormControl isInvalid={tooLong} bg="smBackgroundSecondary" borderRadius="md" p="4">
          <Text id={answerLabelId} fontSize="sm" fontWeight="medium" mb="2">
            {question.subject ? t("Your comment") : answerLabel(answerType, t)}
          </Text>
          {answerType === MarketDialogueAnswerType.FreeText && (
            <>
              <TipTap
                ariaLabel={`${number}. ${question.text}`}
                content={answer.text}
                editable={canAnswer}
                onUpdate={(text) => onChange({ ...answer, text })}
                toolbar={answerToolbar}
                debounceWait={0}
                maxHeight="64"
              />
              {canAnswer && <AnswerLengthHint answer={answer} />}
            </>
          )}
          {answerType === MarketDialogueAnswerType.YesNoComment && (
            <>
              <HStack spacing="2" role="group" aria-labelledby={`${titleId} ${answerLabelId}`}>
                <Button
                  size="sm"
                  variant={answer.choice === true ? "solid" : "outline"}
                  aria-pressed={answer.choice === true}
                  colorScheme="teal"
                  isDisabled={!canAnswer}
                  onClick={() => onChange({ ...answer, choice: true })}
                >
                  {t("Yes")}
                </Button>
                <Button
                  size="sm"
                  variant={answer.choice === false ? "solid" : "outline"}
                  aria-pressed={answer.choice === false}
                  colorScheme={answer.choice === false ? "orange" : "teal"}
                  isDisabled={!canAnswer}
                  onClick={() => onChange({ ...answer, choice: false })}
                >
                  {t("No")}
                </Button>
              </HStack>
              <Textarea
                mt="3"
                rows={3}
                bg="smBackground"
                placeholder={t("Comment (optional)") ?? undefined}
                aria-label={`${number}. ${question.text}: ${t("Comment (optional)")}`}
                isReadOnly={!canAnswer}
                value={answer.text}
                onChange={(e) => onChange({ ...answer, text: e.target.value })}
              />
              {canAnswer && <AnswerLengthHint answer={answer} />}
            </>
          )}
          {answerType === MarketDialogueAnswerType.Document && canAnswer && (
            <Text fontSize="sm" color="smMuted" mb="2">
              {t("Answer this question by uploading the document the buyer asked for.")}
            </Text>
          )}
          {answerType === MarketDialogueAnswerType.Document && !canAnswer && documentCount === 0 && (
            <Text fontSize="sm" color="smMuted">
              {t("No document was uploaded.")}
            </Text>
          )}
          {takesDocuments(question) && (canAnswer || documentCount > 0) && (
            <Box mt={answerType === MarketDialogueAnswerType.Document ? "0" : "4"}>
              <HStack spacing="2" align="center" mb="2">
                <Icon as={FiFileText} color="smMuted" aria-hidden />
                <Text fontSize="sm" fontWeight="semibold">
                  {t("Documentation")}
                </Text>
              </HStack>
              {attachments}
              {documents.map((document) => (
                <BusinessSecretMark
                  key={document.id}
                  label={t("«{{name}}» is a business secret", { name: document.name })}
                  readOnlyLabel={t("«{{name}}» is marked as a business secret", { name: document.name })}
                  reason={answer.secretDocuments[document.id] ?? null}
                  isReadOnly={!canAnswer}
                  onChange={(reason) => onChange({ ...answer, secretDocuments: withSecret(answer.secretDocuments, document.id, reason) })}
                />
              ))}
            </Box>
          )}
          <BusinessSecretMark
            label={t("This answer is a business secret")}
            readOnlyLabel={t("The answer is marked as a business secret")}
            reason={answer.secretReason}
            isReadOnly={!canAnswer}
            onChange={(secretReason) => onChange({ ...answer, secretReason })}
          />
        </FormControl>
      </CardBody>
    </Card>
  );
};

const AnswerLengthHint = ({ answer }: { answer: QuestionAnswerState }) => {
  const { t } = useTranslation();
  const visibleLength = tiptapVisibleLength(answer.text);
  if (visibleLength <= MAX_ANSWER_LENGTH && hasTooMuchFormatting(answer)) {
    return (
      <FormErrorMessage>
        {t("This answer has too much formatting to be saved. Remove some lists, bold text or empty paragraphs.")}
      </FormErrorMessage>
    );
  }
  return <TextLengthHint text={answer.text} length={visibleLength} max={MAX_ANSWER_LENGTH} />;
};

const answerLabel = (answerType: MarketDialogueAnswerType, t: TFunction) => {
  switch (answerType) {
    case MarketDialogueAnswerType.FreeText:
      return t("Your answer (free text)");
    case MarketDialogueAnswerType.YesNoComment:
      return t("Your answer?");
    case MarketDialogueAnswerType.Document:
      return t("Your answer");
  }
};

const withSecret = (secrets: Record<string, string>, documentId: string, reason: string | null) => {
  const { [documentId]: _, ...others } = secrets;
  return reason === null ? others : { ...others, [documentId]: reason };
};
